import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import useActiveSection from "active-section";
import { getService } from "./services-config";
import SEO from "components/SEO/SEO.js";
import GeneNavbar from "components/Navbars/GeneNavbar.js";
import DemoFooter from "components/Footers/DemoFooter.js";
import { Button, Container, Row, Col } from "reactstrap";
import renderFormattedText from "./renderFormattedText";
import renderProcessSteps from "./renderProcessSteps";
import InheritanceDiagram from "./InheritanceDiagram";
import CellFreeDnaDiagram from "./CellFreeDnaDiagram";
import PedigreeDiagram from "./PedigreeDiagram";
import FaqItem from "./FaqItem";
import ServiceIcon from "./ServiceIcon";

// A service gets a diagram section when it has both a `diagram.title` string
// and a component here. Keeping the two conditions separate means translation
// work and component work can land independently without a half-rendered
// section appearing on the live page.
const SERVICE_DIAGRAMS = {
  carrierScreening: InheritanceDiagram,
  hiddenScreening: InheritanceDiagram,
  prenatalScreening: CellFreeDnaDiagram,
  hiddenPrenatal: CellFreeDnaDiagram,
  reproductiveRisk: PedigreeDiagram,
};

// Which kinds of screening a service page is a hub for, in the order they
// appear. `id` is the anchor the jump nav links to, so it is part of the URL
// and should not be renamed casually.
//
// `diagram` is per kind rather than per page, and optional: on the prenatal
// page only NIPT has a figure, because only NIPT has a mechanism a picture
// explains better than a sentence. `rows` is passed through to the diagram for
// the ones that take it.
const SCREENING_SECTIONS = {
  carrierScreening: [
    {
      key: "carrier",
      id: "carrier-screening",
      diagram: InheritanceDiagram,
      rows: ["autosomal", "xlinked"],
    },
    {
      key: "hereditaryCancer",
      id: "hereditary-cancer",
      diagram: InheritanceDiagram,
      rows: ["dominant"],
    },
  ],
  prenatalScreening: [
    { key: "nipt", id: "nipt", diagram: CellFreeDnaDiagram },
    { key: "fts", id: "first-trimester-screening" },
    { key: "serum", id: "maternal-serum-screening" },
    { key: "ultrasound", id: "ultrasound" },
  ],
};

// Where a service's diagram sits. The default is straight after `details`,
// where the inheritance and cell-free DNA diagrams illustrate the biology the
// paragraphs above them just described.
//
// The pedigree is a different kind of picture. It explains no mechanism; it is
// a sample of something the reader is handed, so it belongs after "What to
// Expect", which is where the copy says a pedigree will be drawn and kept in
// their summary package.
const DIAGRAM_AFTER = {
  reproductiveRisk: "whatToExpect",
};
const DEFAULT_DIAGRAM_AFTER = "details";

// Section backgrounds are keyed to what a section is, not where it sits in the
// array. The previous `index % 2` alternation meant adding or hiding one
// section silently re-tinted every section below it.
//
// Two surfaces, per the brand guide: white, and the brand's one light teal.
// The tint marks the opening and the close of the page. `lift` is the third
// case — a section whose content is a white card, which needs the tint behind
// it to read as raised.
const TONE_CLASS = {
  hero: "service-section--hero",
  lift: "service-section--lift",
  plain: "",
  cta: "service-section--cta",
};

// Below this a jump nav costs more than the scrolling it saves. Deliberately
// per-page rather than shared: the two pages that have a jump nav are free to
// disagree about when one is worth it. ResourcesPage.js declares its own, and
// resources-config.test.js pins that page's group count above this number —
// change one and check the other.
const MIN_NAV_ENTRIES = 3;

function ServicePage({ serviceKey }) {
  const { t, i18n } = useTranslation();
  const { search } = useLocation();
  const service = getService(serviceKey);

  // The jump nav makes /proactive-genetic-screening#faq a shareable URL, and
  // landing on the FAQ — like landing at the top of a service reached from the
  // nav, or back where the reader left off — is <ScrollRestoration>'s job in
  // routes.js.
  // It runs after this page has been laid out, which is what the browser's own
  // fragment handling cannot do: on a cold load it looks for the target while
  // the document is still an empty root div, and gives up.

  const sections = [];

  if (service) {
    sections.push({
      id: "hero",
      tone: "hero",
      content: (
        <Container>
          <Row className={service.image ? "align-items-center" : ""}>
            <Col
              className={service.image ? "ml-auto" : "ml-auto mr-auto"}
              md={service.image ? "6" : "8"}
            >
              {/* Drawn on arrival rather than on scroll. This section is the
                  first on the page and so is already on screen at load, which
                  means the reveal observer resolves it to "shown" on its first
                  callback and it never receives a hidden dash state to animate
                  out of — scroll-reveal was never the right trigger for
                  something already visible. The keyframe in _motion.scss draws
                  it on mount instead, which is also what the brand mark in the
                  navbar above it does. */}
              <div className="service-hero-icon">
                <ServiceIcon name={service.icon} size={30} draw />
              </div>
              <h2 className="service-hero-title">
                {t(`landing.service.${serviceKey}.title`)}
              </h2>
              <p className="service-hero-lede">
                {t(`landing.service.${serviceKey}.body`)}
              </p>
            </Col>
            {service.image && (
              <Col className="mr-auto text-center" md="6">
                <img
                  src={service.image}
                  srcSet={service.imageSrcSet}
                  sizes="(max-width: 575.98px) calc(100vw - 30px), (max-width: 767.98px) 510px, (max-width: 991.98px) 330px, (max-width: 1199.98px) 450px, 540px"
                  alt=""
                  className="service-hero-image"
                  style={
                    service.imagePosition
                      ? { objectPosition: service.imagePosition }
                      : undefined
                  }
                />
              </Col>
            )}
          </Row>
        </Container>
      ),
    });

    // Titled and listed in the nav like every other section. It used to be
    // neither, which made it the one long stretch of the page a reader could be
    // in without the jump nav agreeing: the scrollspy had no entry to highlight
    // here, so it highlighted whatever came next, and the reader was told they
    // were somewhere they had not reached yet.
    sections.push({
      id: "details",
      tone: "plain",
      navLabel: t("landing.serviceSection.overview.title"),
      content: (
        <Container>
          <Row>
            <Col md="10">
              <span className="service-rule" aria-hidden="true" />
              <h2 className="service-section-title">
                {t("landing.serviceSection.overview.title")}
              </h2>
              {renderFormattedText(t(`landing.service.${serviceKey}.details`))}
            </Col>
          </Row>
        </Container>
      ),
    });

    // A service whose page is a hub of screening types gets one titled section
    // per type instead of a single diagram section: the summary stays general
    // so the page can take a third type, and each type carries its own copy and
    // its own inheritance pattern. Adding one is an entry in SCREENING_SECTIONS
    // plus its strings; nothing here needs to change.
    const screeningSections = SCREENING_SECTIONS[serviceKey] || [];
    screeningSections.forEach((screen) => {
      const base = `landing.service.${serviceKey}.screening.${screen.key}`;
      if (!i18n.exists(`${base}.heading`)) return;
      // Capitalised so JSX reads it as a component rather than an element name.
      const SectionDiagram = screen.diagram;
      sections.push({
        id: screen.id,
        tone: "plain",
        navLabel: t(`${base}.heading`),
        content: (
          <Container>
            <Row>
              <Col md="10">
                <span className="service-rule" aria-hidden="true" />
                <h2 className="service-section-title">{t(`${base}.heading`)}</h2>
                {renderFormattedText(t(`${base}.body`))}
                {/* `bare`: this section has already titled the figure, and a
                    second h2 inside it would be both a duplicate heading and a
                    heading nested in its own section. */}
                {SectionDiagram && (
                  <SectionDiagram
                    serviceKey={serviceKey}
                    rows={screen.rows}
                    shellBase={`${base}.diagram`}
                    bare
                  />
                )}
              </Col>
            </Row>
          </Container>
        ),
      });
    });

    const Diagram = SERVICE_DIAGRAMS[serviceKey];
    const diagramSection =
      screeningSections.length === 0 &&
      Diagram &&
      i18n.exists(`landing.service.${serviceKey}.diagram.title`)
        ? {
            id: "diagram",
            tone: "plain",
            navLabel: t(`landing.service.${serviceKey}.diagram.title`),
            content: (
              <Container>
                <Row>
                  <Col md="10">
                    {/* The diagram supplies its own heading, so this section
                        takes only the rule that marks every other section
                        opening. */}
                    <span className="service-rule" aria-hidden="true" />
                    <Diagram serviceKey={serviceKey} />
                  </Col>
                </Row>
              </Container>
            ),
          }
        : null;

    const diagramAfter =
      DIAGRAM_AFTER[serviceKey] || DEFAULT_DIAGRAM_AFTER;

    // Called at each of the places a diagram is allowed to sit, so the section
    // lands in exactly one of them and the jump nav follows without a second
    // ordering rule to keep in step.
    const pushDiagramAfter = (sectionId) => {
      if (diagramSection && diagramAfter === sectionId) {
        sections.push(diagramSection);
      }
    };

    pushDiagramAfter("details");

    if (i18n.exists(`landing.service.${serviceKey}.whenToReachOut`)) {
      const bullets = t(
        `landing.service.${serviceKey}.whenToReachOutBulletsArray`,
        { returnObjects: true }
      );
      sections.push({
        id: "whenToReachOut",
        tone: "lift",
        navLabel: t(`landing.service.${serviceKey}.whenToReachOut`),
        content: (
          <Container>
            <Row>
              <Col md="10">
                {/* The qualifying question — the one lifted element on the
                    page, so a visitor deciding whether this service is for
                    them can find it without reading the whole page. */}
                <div className="service-card">
                  <span className="service-rule" aria-hidden="true" />
                  <h2 className="service-section-title">
                    {t(`landing.service.${serviceKey}.whenToReachOut`)}
                  </h2>
                  <p className="service-section-lede">
                    {t(`landing.service.${serviceKey}.whenToReachOutSubtitle`)}
                  </p>
                  <ul className="service-checks">
                    {(Array.isArray(bullets) ? bullets : []).map(
                      (item, index) => (
                        <li key={index}>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="M2.5 8.5l3.2 3.4 7.8-8"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span>{item}</span>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </Col>
            </Row>
          </Container>
        ),
      });
    }

    if (i18n.exists(`landing.service.${serviceKey}.whatToExpect.body`)) {
      sections.push({
        id: "whatToExpect",
        tone: "plain",
        navLabel: t("landing.serviceSection.whatToExpect.title"),
        content: (
          <Container>
            <Row>
              <Col md="10">
                <span className="service-rule" aria-hidden="true" />
                <h2 className="service-section-title">
                  {t("landing.serviceSection.whatToExpect.title")}
                </h2>
                {renderFormattedText(
                  t(`landing.service.${serviceKey}.whatToExpect.body`)
                )}
              </Col>
            </Row>
          </Container>
        ),
      });
    }

    pushDiagramAfter("whatToExpect");

    if (i18n.exists(`landing.service.${serviceKey}.process.body`)) {
      sections.push({
        id: "process",
        tone: "plain",
        navLabel: t("landing.serviceSection.process.title"),
        content: (
          <Container>
            <Row>
              <Col md="10">
                <span className="service-rule" aria-hidden="true" />
                <h2 className="service-section-title">
                  {t("landing.serviceSection.process.title")}
                </h2>
                {renderProcessSteps(
                  t(`landing.service.${serviceKey}.process.body`)
                )}
              </Col>
            </Row>
          </Container>
        ),
      });
    }

    if (i18n.exists(`landing.service.${serviceKey}.faq.items`)) {
      const faqItems = t(`landing.service.${serviceKey}.faq.items`, {
        returnObjects: true,
      });
      sections.push({
        id: "faq",
        tone: "plain",
        navLabel: t("landing.serviceSection.faq.title"),
        content: (
          <Container>
            <Row>
              <Col md="10">
                <span className="service-rule" aria-hidden="true" />
                <h2 className="service-section-title">
                  {t("landing.serviceSection.faq.title")}
                </h2>
                {/* Collapsed by default: six open entries pushed the closing
                    call to action roughly a screen and a half further down. */}
                <div className="service-faq">
                  {(Array.isArray(faqItems) ? faqItems : []).map((item, i) => (
                    <FaqItem key={i} question={item.q} answer={item.a} />
                  ))}
                </div>
              </Col>
            </Row>
          </Container>
        ),
      });
    }

    sections.push({
      id: "contact",
      tone: "cta",
      content: (
        <Container>
          <Row>
            <Col className="ml-auto mr-auto text-center" md="8">
              <h2 className="service-cta-title">{t("landing.haveQuestions")}</h2>
              <p className="service-cta-lede">{t("landing.contactPrompt")}</p>
              <Button
                className="btn-fill btn-round"
                color="info"
                size="lg"
                href="/index#contact"
              >
                {t("landing.getInTouch")}
              </Button>
            </Col>
          </Row>
        </Container>
      ),
    });
  }

  const navEntries = sections.filter((section) => section.navLabel);
  const showNav = navEntries.length >= MIN_NAV_ENTRIES;
  // useActiveSection keys its effect on the joined ids, so a fresh array
  // identity each render costs nothing.
  const activeId = useActiveSection(
    navEntries.map((entry) => entry.id),
    serviceKey
  );

  if (!service) return null;

  // SEO.js appends the brand suffix to `title` itself, so this passes the bare
  // page name; passing a pre-branded string put the suffix in twice. It does not
  // append to `ogTitle`, which wants the brand in it for a social card, so that
  // one is branded here. Landing and Resources already pass bare names.
  const brandedTitle = `${service.seo.title} | GeneFlou Genetic Counselling - Helping Families Flourish`;

  return (
    <>
      <SEO
        title={service.seo.title}
        description={service.seo.description}
        keywords={service.seo.keywords}
        canonicalUrl={service.seo.canonicalUrl}
        ogUrl={service.seo.ogUrl}
        ogTitle={brandedTitle}
        ogDescription={service.seo.description}
        noindex={service.noindex}
      />
      <GeneNavbar />
      <div className="main service-detail">
        {sections.map((section) => (
          <React.Fragment key={section.id}>
            {section.id === "details" && showNav && (
              <nav
                className="service-jump"
                aria-label={t("landing.serviceSection.onThisPage")}
              >
                {/* Same Container as every section, so the nav starts on the
                    page's single left axis rather than at the viewport edge. */}
                <Container>
                  {/* <Link>, not a bare <a href="#...">. A plain fragment link
                      is a navigation the browser makes on its own: it pushes a
                      history entry the router never learns about, and since
                      <ScrollRestoration> has taken restoration off the browser,
                      going back to one moved the URL and nothing else. Routing
                      these through the router puts those entries under the same
                      ownership as every other one. */}
                  <ul>
                    {navEntries.map((entry) => (
                      <li key={entry.id}>
                        <Link
                          // `search` is carried explicitly: a `to` of just a
                          // fragment keeps the path but drops the query, which
                          // would strip campaign parameters off a shared link
                          // the moment the reader used the nav.
                          to={{ hash: `#${entry.id}`, search }}
                          className={
                            entry.id === activeId ? "is-active" : undefined
                          }
                          aria-current={
                            entry.id === activeId ? "true" : undefined
                          }
                        >
                          {entry.navLabel}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Container>
              </nav>
            )}
            <section
              id={section.id}
              className={`section service-section ${TONE_CLASS[section.tone]}`}
            >
              {section.content}
            </section>
          </React.Fragment>
        ))}
      </div>
      <DemoFooter />
    </>
  );
}

export default ServicePage;
